import { Address } from '@awo-care/shared-core';
import { GeoPoint } from 'firebase/firestore';

export interface AddressEditFields {
  street: string;
  houseNumber: string;
  zipCode: string;
  city: string;
}

const HOUSE_NUMBER_REGEX = /^(.*?)\s+(\d[\w\-/]*)$/;

export function splitStreetAndNumber(combined: string): {
  street: string;
  houseNumber: string;
} {
  const match = combined.trim().match(HOUSE_NUMBER_REGEX);
  if (match) {
    return { street: match[1].trim(), houseNumber: match[2].trim() };
  }
  return { street: combined.trim(), houseNumber: '' };
}

export function joinStreetAndNumber(street: string, houseNumber: string): string {
  return [street.trim(), houseNumber.trim()].filter(Boolean).join(' ');
}

export function formatManualAddress(input: AddressEditFields): string {
  const fullStreet = joinStreetAndNumber(input.street, input.houseNumber);
  const locality = [input.zipCode.trim(), input.city.trim()].filter(Boolean).join(' ');
  return [fullStreet, locality].filter(Boolean).join(', ');
}

export function hasAddressFieldsChanged(
  base: Pick<Address, 'street' | 'zipCode' | 'city'>,
  edits: AddressEditFields,
): boolean {
  const baseParts = splitStreetAndNumber(base.street);
  return (
    baseParts.street !== edits.street.trim() ||
    baseParts.houseNumber !== edits.houseNumber.trim() ||
    base.zipCode !== edits.zipCode.trim() ||
    base.city !== edits.city.trim()
  );
}

export function buildManualAddress(
  edits: AddressEditFields,
  placeholderPosition: GeoPoint,
): Address {
  return {
    street: joinStreetAndNumber(edits.street, edits.houseNumber),
    zipCode: edits.zipCode.trim(),
    city: edits.city.trim(),
    state: '',
    country: 'Germany',
    countryCode: 'DE',
    position: placeholderPosition,
    formattedAddress: formatManualAddress(edits),
    isValid: false,
    addressManuallyEntered: true,
  };
}

export function applyManualEdit(base: Address, edits: AddressEditFields): Address {
  if (!hasAddressFieldsChanged(base, edits)) {
    return base;
  }
  return {
    ...base,
    street: joinStreetAndNumber(edits.street, edits.houseNumber),
    zipCode: edits.zipCode.trim(),
    city: edits.city.trim(),
    formattedAddress: formatManualAddress(edits),
    isValid: false,
    addressManuallyEntered: true,
  };
}

export function adoptGoogleMatch(googleAddress: Address): Address {
  return {
    ...googleAddress,
    addressManuallyEntered: false,
  };
}

export function extractEditFields(address: Address | null | undefined): AddressEditFields {
  const parts = splitStreetAndNumber(address?.street ?? '');
  return {
    street: parts.street,
    houseNumber: parts.houseNumber,
    zipCode: address?.zipCode ?? '',
    city: address?.city ?? '',
  };
}
